import { type Ref, type RefCallback, useCallback, useRef } from 'react'

function useMergedRef<T>(...refs: (Ref<T> | undefined | null)[]): RefCallback<T> {
  const stableRefs = useRef(refs)
  const didRefsChange =
    stableRefs.current.length !== refs.length ||
    stableRefs.current.some((ref, i) => ref !== refs[i])

  if (didRefsChange) {
    stableRefs.current = refs
  }

  return useCallback((element: T) => {
    const cleanups: (() => void)[] = []

    for (const ref of stableRefs.current) {
      if (!ref) continue

      if (typeof ref === 'function') {
        const cleanup = ref(element)

        if (typeof cleanup === 'function') {
          cleanups.push(cleanup)
        } else {
          cleanups.push(() => ref(null))
        }
      } else if (typeof ref === 'object') {
        ref.current = element

        cleanups.push(() => {
          ref.current = null
        })
      }
    }

    return () => {
      for (const cleanup of cleanups) {
        cleanup()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stableRefs.current])
}

export default useMergedRef